import React from "react";
import { Trans } from "react-i18next";
import { Drawer } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { MdArrowBack } from "react-icons/md";
import classnames from "classnames";
import { observer } from "mobx-react";
import { getDirection } from "../../../helpers";
import { useStore } from "../../../store/rootStore";
import { DrawerLevel, ManagerFeature, ManagerOperation } from "../../../store/UIStore";
import { Operation } from "../../EntityManager";
import LabelsDetailsView from "../managers/labels/LabelsDetailsView";
import { ChannelsDetailsView } from "../managers/channels";
import { AgentsDetailsView } from "../managers/agents";
import { DepartmentsDetailsView } from "../managers/departments";
import { ApiTokensDetailsView } from "../managers/apiTokens";
import { OAuthDetailsView } from "../managers/oauth";
import styles from "../SettingsDrawer.module.scss";
const FEATURE_TITLES = {
    [ManagerFeature.LABELS]: {
        [ManagerOperation.VIEW]: "labelDetails",
        [ManagerOperation.CREATE]: "createLabel",
        [ManagerOperation.EDIT]: "editLabel",
        [ManagerOperation.DELETE]: "deleteLabel",
    },
    [ManagerFeature.AGENTS]: {
        [ManagerOperation.VIEW]: "agentDetails",
        [ManagerOperation.CREATE]: "createAgent",
        [ManagerOperation.EDIT]: "editAgent",
        [ManagerOperation.DELETE]: "deleteAgent",
    },
    [ManagerFeature.DEPARTMENTS]: {
        [ManagerOperation.VIEW]: "departmentDetails",
        [ManagerOperation.CREATE]: "createDepartment",
        [ManagerOperation.EDIT]: "editDepartment",
        [ManagerOperation.DELETE]: "deleteDepartment",
    },
    [ManagerFeature.CHANNELS]: {
        [ManagerOperation.VIEW]: "channelDetails",
        [ManagerOperation.CREATE]: "createChannel",
        [ManagerOperation.EDIT]: "editChannel",
        [ManagerOperation.DELETE]: "deleteChannel",
    },
    [ManagerFeature.API_TOKENS]: {
        [ManagerOperation.VIEW]: "apiTokenDetails",
        [ManagerOperation.CREATE]: "createApiToken",
        [ManagerOperation.EDIT]: "editApiToken",
        [ManagerOperation.DELETE]: "deleteApiToken",
    },
    [ManagerFeature.OAUTH]: {
        [ManagerOperation.VIEW]: "oauthDetails",
        [ManagerOperation.CREATE]: "OAuthAuthorize",
        [ManagerOperation.EDIT]: "oauthDetails",
        [ManagerOperation.DELETE]: "deleteOAuthCred",
    },
};
const operationMap = {
    [ManagerOperation.VIEW]: Operation.LIST,
    [ManagerOperation.CREATE]: Operation.CREATE,
    [ManagerOperation.EDIT]: Operation.EDIT,
    [ManagerOperation.DELETE]: Operation.DELETE,
};
const ThirdLevelDrawer = observer(({ isOpen, className, onCloseDrawer, onListRefresh, }) => {
    var _a, _b, _c;
    const { ui } = useStore();
    const currentFeature = ui.getManagerFeature();
    const itemId = ui.getManagerItemId();
    const managerOperation = ui.getManagerOperation();
    const lastStateRef = React.useRef(null);
    if (currentFeature && managerOperation) {
        const isValidState = managerOperation === ManagerOperation.CREATE || itemId;
        if (isValidState) {
            lastStateRef.current = {
                feature: currentFeature,
                itemId,
                operation: managerOperation,
            };
        }
    }
    const displayState = (currentFeature && managerOperation)
        ? { feature: currentFeature, itemId, operation: managerOperation }
        : lastStateRef.current;
    const title = displayState
        ? ((_a = FEATURE_TITLES[displayState.feature]) === null || _a === void 0 ? void 0 : _a[displayState.operation]) || "Details"
        : "Details";
    const operation = displayState ? operationMap[displayState.operation] : Operation.LIST;
    const displayItemId = (_b = displayState === null || displayState === void 0 ? void 0 : displayState.itemId) !== null && _b !== void 0 ? _b : null;
    const displayFeature = (_c = displayState === null || displayState === void 0 ? void 0 : displayState.feature) !== null && _c !== void 0 ? _c : null;
    const handleCancel = () => {
        ui.closeManagerDrawer(DrawerLevel.THIRD);
    };
    const handleCompleted = () => {
        onListRefresh === null || onListRefresh === void 0 ? void 0 : onListRefresh();
        ui.closeManagerDrawer(DrawerLevel.THIRD);
    };
    const renderLoading = () => (React.createElement("div", { className: styles.loadingContainer },
        React.createElement(LoadingOutlined, { className: styles.loadingSpinner })));
    const renderContent = () => {
        if (!displayFeature) {
            return renderLoading();
        }
        switch (displayFeature) {
            case ManagerFeature.LABELS:
                return (React.createElement(LabelsDetailsView, { entityId: displayItemId, operation: operation, onCancel: handleCancel, onCompleted: handleCompleted }));
            case ManagerFeature.CHANNELS:
                return (React.createElement(ChannelsDetailsView, { entityId: displayItemId, operation: operation, onCancel: handleCancel, onCompleted: handleCompleted }));
            case ManagerFeature.AGENTS:
                return (React.createElement(AgentsDetailsView, { entityId: displayItemId, operation: operation, onCancel: handleCancel, onCompleted: handleCompleted }));
            case ManagerFeature.DEPARTMENTS:
                return (React.createElement(DepartmentsDetailsView, { entityId: displayItemId, operation: operation, onCancel: handleCancel, onCompleted: handleCompleted }));
            case ManagerFeature.API_TOKENS:
                return (React.createElement(ApiTokensDetailsView, { entityId: displayItemId, operation: operation, onCancel: handleCancel, onCompleted: handleCompleted }));
            case ManagerFeature.OAUTH:
                return (React.createElement(OAuthDetailsView, { entityId: displayItemId, operation: operation, onCancel: handleCancel, onCompleted: handleCompleted }));
            default:
                return renderLoading();
        }
    };
    // Tried best way to calculate the width of the drawer, this works great even tho it looks madeup
    const thirdLevelWidth = Math.min(848, window.innerWidth - 64 - 320 - 368);
    return (React.createElement(Drawer, { className: classnames(className, styles.drawer, styles.thirdLevel, { [styles.rtl]: getDirection() === "rtl" }), onClose: onCloseDrawer, placement: getDirection() === "rtl" ? "right" : "left", open: isOpen, width: thirdLevelWidth, title: React.createElement(Trans, { i18nKey: title }), bodyStyle: { padding: 0 }, closeIcon: React.createElement(MdArrowBack, { className: styles.closeIcon }), maskClosable: true }, renderContent()));
});
export default ThirdLevelDrawer;
